import { Button } from '@/components/ui/button';
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@/components/ui/modal';
import { paths } from '@/routes/paths';
import ConfirmEmailForAccess from '../ConfirmEmailForAccess';

interface InstitutionalAccessClaimModalProps {
  open: boolean;
  claimCopy?: string | null;
  schoolNames: string;
  accessEndDate?: string | null;
  claimError?: string | null;
  isClaiming: boolean;
  onClaim: () => void;
  /** The roster seat waits for a confirmed email (ORA-994). */
  emailVerificationRequired?: boolean;
  /** Reloads the claim options; resolves true while still unconfirmed. */
  onRecheckEmail?: () => Promise<boolean>;
  /** Hides the confirm-email state (it alone has a soft exit). */
  onNotNowEmail?: () => void;
}

export const CONFIRM_EMAIL_FOR_ACCESS_TITLE =
  'Confirm Your Email to Get Your Free Access';

const InstitutionalAccessClaimModal = ({
  open,
  claimCopy,
  schoolNames,
  accessEndDate,
  claimError,
  isClaiming,
  onClaim,
  emailVerificationRequired = false,
  onRecheckEmail,
  onNotNowEmail,
}: InstitutionalAccessClaimModalProps) => (
  <Modal
    open={open}
    onClose={() => {
      return;
    }}
    name="institutional-access-claim"
    title="Claim Institution Access"
    description="Claim institution-purchased Ora access."
    disableBackdropClick={true}
    disableEscapeKeyDown={true}
  >
    {emailVerificationRequired ? (
      <>
        <ModalHeader>
          <h2>{CONFIRM_EMAIL_FOR_ACCESS_TITLE}</h2>
        </ModalHeader>
        <ModalBody>
          <ConfirmEmailForAccess
            intro={`${schoolNames || 'Your school'} has given you free Ora access.`}
            next={paths.home}
            onRecheck={onRecheckEmail ?? (async () => true)}
            context="modal"
            onNotNow={onNotNowEmail}
          />
        </ModalBody>
      </>
    ) : (
      <>
        <ModalHeader>
          <h2>Claim Ora Access</h2>
        </ModalHeader>
        <ModalBody>
          <p className="!my-0">
            {claimCopy || 'Your institution purchased Ora access for you.'}
          </p>
          {schoolNames && (
            <p className="!mb-0 mt-4 text-sm text-fg-secondary">
              {schoolNames}
              {accessEndDate ? ` - Access through ${accessEndDate}` : ''}
            </p>
          )}
          {claimError && (
            <p className="mb-0 mt-4 text-sm text-status-danger">{claimError}</p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            onClick={onClaim}
            context="modal"
            disabled={isClaiming}
            className="w-full"
          >
            {isClaiming ? 'Claiming...' : 'Claim Access'}
          </Button>
        </ModalFooter>
      </>
    )}
  </Modal>
);

export default InstitutionalAccessClaimModal;
